import { TargetingFields, type TargetingFieldsValues } from '@openx/types/targeting/targeting';
import type { JSX, ReactNode } from 'react';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingDropdownSection } from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';

type AdvancedTargetingSectionReadOnlyProps = {
  name: string | JSX.Element;
  field: TargetingFieldsValues;
  sectionName: string;
  description: string;
  hasValue: boolean;
  readonly?: boolean;
  onOpenDrawerClick: () => void;
  onRemove?: () => void;
  children: ReactNode;
};

/**
 * Read-only card shared by the Custom Targeting and Contextual Segments (V1/V2) cards.
 * Both respect the `advanced_targeting` blocking rule as well as the rule for their own field.
 */
export const AdvancedTargetingSectionReadOnly = ({
  name,
  field,
  sectionName,
  description,
  hasValue,
  readonly,
  onOpenDrawerClick,
  onRemove,
  children,
}: AdvancedTargetingSectionReadOnlyProps): JSX.Element => {
  const { isDisabled: isAdvancedTargetingDisabled, disabledReasonMessage: advancedTargetingDisabledReasonMessage } =
    useTargetingDisabledState(TargetingFields.ADVANCED_TARGETING);
  // TargetingReadOnly checks the field rule for the card itself; the section buttons need both rules
  const { isDisabled: isFieldDisabled, disabledReasonMessage: fieldDisabledReasonMessage } =
    useTargetingDisabledState(field);
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  const isDisabled = isAdvancedTargetingDisabled || isFieldDisabled;
  const disabledReasonMessage = advancedTargetingDisabledReasonMessage ?? fieldDisabledReasonMessage;

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      isEmpty={!hasValue}
      onOpenDrawerClick={onOpenDrawerClick}
      readonly={readonly}
      isDisabled={isDisabled}
      description={description}
      isSingleChoice
      optionsCount={hasValue ? 1 : 0}
      isOpen={isOpen}
      toggleOpen={toggleOpen}
      disabledReasonMessage={disabledReasonMessage}
    >
      {readonly && hasValue && children}

      {!readonly && hasValue && isOpen && (
        <StyledReadOnlyTargetingDropdown>
          <TargetingDropdownSection
            name={sectionName}
            isEmpty={false}
            onOpenDrawerClick={onOpenDrawerClick}
            onRemove={onRemove}
            isDisabled={isDisabled}
            disabledReasonMessage={disabledReasonMessage}
            hasOptionsSelected
          >
            {children}
          </TargetingDropdownSection>
        </StyledReadOnlyTargetingDropdown>
      )}
    </TargetingReadOnly>
  );
};
